import { CoverageTypeEnum } from "@platform/types";
import { useMemo } from "react";

import { READ_ONLY_CARRIER_IDS } from "../utils/quotesPlanIds";

import { useProposalPlanRiderChargesMutation } from "./useProposalPlanRiderChargesMutation";

import type {
  CarrierBenefitPlanGroup,
  RiderChargeEditor,
} from "@platform/btx-rates";
import type { ProposalScope } from "@platform/mpx-quotes-grid";
import type { AxiosInstance } from "axios";

// Coverages whose quotes carry flat rider charges per covered relationship.
const RIDER_CHARGE_COVERAGE_TYPES: ReadonlySet<string> = new Set([
  CoverageTypeEnum.CRITICAL_ILLNESS,
]);

interface UseRiderChargeEditorOptions {
  rfpId: string;
  axiosInstance: AxiosInstance;
  scope: ProposalScope;
  coverageType: string | undefined;
  /** The rates table's data; undefined until it loads. */
  ratesGridData:
    | { carrierGroups: readonly CarrierBenefitPlanGroup[] }
    | undefined;
  /** Plans with no persisted proposal_plan row yet (placeholder and pad slots). */
  unsavedPlanIds: ReadonlySet<string>;
  onSaveError: (err: Error) => void;
}

/**
 * The rates table's rider-charge editor for the selected coverage, or
 * undefined when the coverage has no rider charges, which hides the rows.
 * Charges save on a carrier's persisted plans; the Incumbent/Requested column
 * and unsaved placeholder slots show theirs read-only.
 */
export const useRiderChargeEditor = ({
  rfpId,
  axiosInstance,
  scope,
  coverageType,
  ratesGridData,
  unsavedPlanIds,
  onSaveError,
}: UseRiderChargeEditorOptions): RiderChargeEditor | undefined => {
  const { saveRiderCharges } = useProposalPlanRiderChargesMutation({
    rfpId,
    axiosInstance,
    scope,
    onSaveError,
  });

  const hasRiderCharges =
    coverageType !== undefined && RIDER_CHARGE_COVERAGE_TYPES.has(coverageType);

  // Keyed by content, not by reference: a new editor rebuilds every rates
  // cell, dropping any draft being typed, so a refetch returning the same plans
  // must not make one.
  const carrierGroups = ratesGridData?.carrierGroups;
  const lockedPlanIdsKey = useMemo(
    () =>
      [
        ...(carrierGroups ?? [])
          .filter((group) => READ_ONLY_CARRIER_IDS.includes(group.carrierId))
          .flatMap((group) =>
            group.benefitPlanGroups.flatMap((planGroup) =>
              planGroup.benefitPlans.map((plan) => plan.id),
            ),
          ),
        ...unsavedPlanIds,
      ]
        .sort()
        .join("\n"),
    [carrierGroups, unsavedPlanIds],
  );

  return useMemo(() => {
    if (!hasRiderCharges) return undefined;
    const lockedPlanIds = new Set(lockedPlanIdsKey.split("\n"));
    return {
      onChange: saveRiderCharges,
      isEditable: (planId) => !lockedPlanIds.has(planId),
    };
  }, [hasRiderCharges, lockedPlanIdsKey, saveRiderCharges]);
};
